import React, { Fragment, useEffect } from "react";
import { identifierPairs } from "../../config/statutoryIdentifiers";
import SEO from "../../components/SEO/SEO";
import { LegalPageLayout, LegalList, LegalListItem } from "../../components/LegalPageLayout";

const DataDeletionPage: React.FC = () => {
  return (
    <Fragment>
    <SEO title="Data Deletion" description="Request deletion of your AXOIX account and business data. Instructions for removing your data from the platform." path="/data-deletion" />
    <LegalPageLayout
      pageTitle="Data Deletion"
      pageSub="Data Deletion"
      lastUpdated="June 22, 2026"
    >
      <h2 className="details-content-title mb-15">1. Overview</h2>
      <p>
        At <strong>Axoix Technologies Private Limited</strong>, we respect your right to control your personal data.
        This page explains how you can request the deletion of your data from AXOIX and
        its connected services, including data accessed through third-party platforms such as
        Facebook, Instagram, Threads, Google, LinkedIn, and Twitter/X.
      </p>

      <h2 className="details-content-title mb-15 mt-50">2. How to Request Data Deletion</h2>
      <p>You can request deletion of your data through any of the following methods:</p>

      <h3 className="item_details_info_heading mb-20 mt-30">2.1 From Your AXOIX Dashboard</h3>
      <LegalList>
        <LegalListItem>Log in to your AXOIX dashboard</LegalListItem>
        <LegalListItem>Go to <strong>Settings &gt; Account &gt; Data &amp; Privacy</strong></LegalListItem>
        <LegalListItem>Click <strong>"Delete My Account and Data"</strong></LegalListItem>
        <LegalListItem>Confirm your identity via email verification</LegalListItem>
        <LegalListItem>Your data deletion request will be processed within 30 days</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">2.2 Via Email</h3>
      <p>
        Send a data deletion request to{" "}
        <a href="mailto:privacy@axoix.com">privacy@axoix.com</a> with the
        following information:
      </p>
      <LegalList>
        <LegalListItem>Your full name and email address associated with AXOIX</LegalListItem>
        <LegalListItem>Your company/organization name (if applicable)</LegalListItem>
        <LegalListItem>A clear statement requesting data deletion</LegalListItem>
        <LegalListItem>Any specific data you want deleted (or "all data")</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">2.3 Disconnecting Social Media Accounts</h3>
      <p>
        To remove social media data specifically (Facebook, Instagram, Threads, Google,
        LinkedIn, Twitter/X):
      </p>
      <LegalList>
        <LegalListItem>Go to <strong>CHORUS &gt; Connected Accounts</strong> in your dashboard</LegalListItem>
        <LegalListItem>Click <strong>"Disconnect"</strong> next to the platform you want to remove</LegalListItem>
        <LegalListItem>All OAuth tokens and cached social media data for that platform will be deleted immediately</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">3. Facebook / Meta Data Deletion</h2>
      <div className="postabmin_block xb-border mt-20 mb-40">
        <div className="admin_content">
          <p>
            <strong>For Facebook, Instagram, and Threads users:</strong> When you remove
            AXOIX from your Facebook settings or request data deletion through Facebook,
            we automatically receive a deletion request and will remove all data associated
            with your Facebook User ID from our systems.
          </p>
        </div>
      </div>

      <h3 className="item_details_info_heading mb-20 mt-30">3.1 How to Remove AXOIX via Facebook</h3>
      <LegalList>
        <LegalListItem>Go to your <strong>Facebook Settings &gt; Security and Login &gt; Apps and Websites</strong></LegalListItem>
        <LegalListItem>Find <strong>"Axoix"</strong> in the list of active apps</LegalListItem>
        <LegalListItem>Click <strong>"Remove"</strong> and confirm</LegalListItem>
        <LegalListItem>Optionally check "Delete all posts, photos, and videos on Facebook that Axoix may have published on your behalf"</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">3.2 Data We Delete for Facebook Users</h3>
      <p>Upon receiving a deletion request from Facebook, we will delete:</p>
      <LegalList>
        <LegalListItem>Your Facebook/Instagram/Threads OAuth access tokens</LegalListItem>
        <LegalListItem>Cached profile information (name, profile picture, page data)</LegalListItem>
        <LegalListItem>Social media analytics data linked to your Facebook User ID</LegalListItem>
        <LegalListItem>Scheduled posts and content drafts for connected Facebook/Instagram pages</LegalListItem>
        <LegalListItem>Message data from the CHORUS unified inbox</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">4. What Gets Deleted</h2>
      <p>When you request full account deletion, the following data is removed:</p>
      <LegalList>
        <LegalListItem><strong>Account Data:</strong> Name, email, phone number, profile information</LegalListItem>
        <LegalListItem><strong>Business Data:</strong> Company records, financial data, inventory, HR records</LegalListItem>
        <LegalListItem><strong>Social Media Data:</strong> All connected platform tokens, cached content, analytics</LegalListItem>
        <LegalListItem><strong>AI Data:</strong> Chat history, voice interaction logs, custom AI configurations</LegalListItem>
        <LegalListItem><strong>Communication Data:</strong> Emails, support tickets, in-app messages</LegalListItem>
        <LegalListItem><strong>Payment Data:</strong> Billing history (subject to legal retention requirements)</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">5. Data Retention Exceptions</h2>
      <p>
        Certain data may be retained even after a deletion request, as required by law:
      </p>
      <LegalList>
        <LegalListItem><strong>Tax and Financial Records:</strong> Retained for up to 8 years as required by Indian tax law</LegalListItem>
        <LegalListItem><strong>Legal Compliance:</strong> Data required for ongoing legal proceedings or regulatory obligations</LegalListItem>
        <LegalListItem><strong>Fraud Prevention:</strong> Minimal data retained to prevent fraudulent re-registration</LegalListItem>
        <LegalListItem><strong>Anonymized Analytics:</strong> Aggregated, non-identifiable usage statistics</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">6. Processing Timeline</h2>
      <LegalList>
        <LegalListItem><strong>Social Media Tokens:</strong> Deleted immediately upon disconnection</LegalListItem>
        <LegalListItem><strong>Account Data:</strong> Deleted within 30 days of confirmed request</LegalListItem>
        <LegalListItem><strong>Backup Systems:</strong> Purged from backups within 90 days</LegalListItem>
        <LegalListItem><strong>Confirmation:</strong> You will receive an email confirming deletion completion</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">7. Contact Us</h2>
      <p>
        For any questions about data deletion or to submit a request:
      </p>
      <LegalList>
        <LegalListItem><strong>Email:</strong> <a href="mailto:privacy@axoix.com">privacy@axoix.com</a></LegalListItem>
        <LegalListItem><strong>Data Protection Officer:</strong> Rajat Behl</LegalListItem>
        <LegalListItem><strong>Company:</strong> Axoix Technologies Private Limited</LegalListItem>
        <LegalListItem><strong>Address:</strong> B-59, Second Floor, Southern Side, Block B, Rama Park Road, Uttam Nagar, New Delhi, Delhi - 110059, India</LegalListItem>
        {identifierPairs().map(([label, value]) => (
          <LegalListItem key={label}><strong>{label}:</strong> {value}</LegalListItem>
        ))}
        <LegalListItem><strong>Response Time:</strong> We acknowledge all requests within 48 hours and complete processing within 30 days</LegalListItem>
      </LegalList>
    </LegalPageLayout>
    </Fragment>
  );
};

export default DataDeletionPage;
